import { Box, Flex, SimpleGrid, Text, TextInput } from '@mantine/core';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { Select } from 'src/design-system/Select';
import { IconCheck, IconPencil, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { generateFamilyName, V2MemberRelationship } from './dataHelpers';
import { Relation } from '../hooks/useFamily';

const RELATION_OPTIONS: { value: Relation; label: string }[] = [
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Child', label: 'Child' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Other', label: 'Other' },
];

type Props = {
  selectedMembers: Client[];
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (familyName: string, relationships: Record<string, V2MemberRelationship>) => void;
  isLoading: boolean;
};

export default function NameAndRelationshipsStep({
  selectedMembers,
  onBack,
  onCancel,
  onSubmit,
  isLoading,
}: Props) {
  const [familyName, setFamilyName] = useState(generateFamilyName(selectedMembers));
  const [focused, setFocused] = useState(false);
  const [relationships, setRelationships] = useState<Record<string, V2MemberRelationship>>(() =>
    Object.fromEntries(
      selectedMembers.map(m => {
        const others = selectedMembers.filter(o => o.id !== m.id);
        return [m.id, { relation: null, toMemberId: others.length === 1 ? others[0].id : null }];
      }),
    ),
  );

  const setRelation = (memberId: string, relation: Relation | null) => {
    setRelationships(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], relation },
    }));
  };

  const setToMember = (memberId: string, toMemberId: string | null) => {
    setRelationships(prev => ({
      ...prev,
      [memberId]: { ...prev[memberId], toMemberId },
    }));
  };

  const otherMemberOptions = (currentId: string) =>
    selectedMembers
      .filter(m => m.id !== currentId)
      .map(m => ({
        value: m.id,
        label: formatName(m.firstName, m.lastName, NameFormat.NATURAL),
      }));

  const isValid = familyName.trim().length > 0;

  return (
    <Flex direction="column" h="100%">
      <FlowLayout
        header={
          <Box px="xl" pt="xxl" pb="md">
            <Flex justify="space-between" align="center" pb="lg">
              <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                Name this family and define relationships
              </Text>
              <DesignButton variant="text" style={{ flexShrink: 0 }} onClick={onCancel}>
                Cancel
              </DesignButton>
            </Flex>

            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb={6}>
              Family name
            </Text>
            <TextInput
              value={familyName}
              onChange={e => setFamilyName(e.currentTarget.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              rightSection={
                isValid ? (
                  <IconCheck size={18} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconX size={18} color="var(--mantine-color-red-6)" />
                )
              }
              radius="xl"
              size="md"
              styles={theme => ({
                wrapper: { height: 48 },
                input: {
                  height: 48,
                  borderRadius: 9999,
                  backgroundColor: theme.white,
                  border: `2px solid ${focused ? theme.colors.brand[6] : theme.colors.gray[2]}`,
                  fontSize: theme.fontSizes.md,
                },
                section: {
                  width: 44,
                  justifyContent: 'center',
                },
              })}
            />
          </Box>
        }
        body={
          <FlowBody
            footerAlign="center"
            footer={
              <FlowNavigationFooter
                onBack={onBack}
                onAction={() => onSubmit(familyName, relationships)}
                actionDisabled={!isValid}
                actionLoading={isLoading}
              />
            }
          >
            <Box px="xs" pb="md">
              <SimpleGrid cols={2} spacing="sm">
                {selectedMembers.map(client => (
                  <Box key={client.id} bg="gray.1" style={{ borderRadius: 20, padding: 20 }}>
                    <Flex justify="space-between" align="flex-start" mb="md">
                      <Box>
                        <Text fw={FONT_WEIGHTS.semibold} size="md" c="gray.10">
                          {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                        </Text>
                        {client.emailAddress && (
                          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
                            {client.emailAddress}
                          </Text>
                        )}
                      </Box>
                      {client.advised && (
                        <DesignButton
                          variant="tertiary"
                          size="sm"
                          rightSection={<IconPencil size={16} color="currentColor" />}
                          style={{ flexShrink: 0 }}
                        >
                          Advised
                        </DesignButton>
                      )}
                    </Flex>

                    <SimpleGrid cols={2} spacing="xs">
                      <Select
                        label="Relationship"
                        placeholder="Select"
                        data={RELATION_OPTIONS}
                        value={relationships[client.id]?.relation ?? null}
                        onChange={val => setRelation(client.id, val as Relation | null)}
                        variant="card"
                      />
                      <Select
                        label="To"
                        placeholder="Select"
                        data={otherMemberOptions(client.id)}
                        value={relationships[client.id]?.toMemberId ?? null}
                        onChange={val => setToMember(client.id, val)}
                        variant="card"
                      />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </FlowBody>
        }
      />
    </Flex>
  );
}
